import React, { useMemo } from "react";
import classNames from "classnames";

import { IPager } from "types";
import { ChevronLeft, ChevronRight } from "assets/icons";
import { i18nService } from "services";

const NB_VISIBLE_PAGES = 10;

interface PagerProps extends IPager {
  theme?: "dark" | "light";
}

export const Pager: React.FC<PagerProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  theme = "light",
}) => {
  const showPrevButton = useMemo(() => currentPage > 1, [currentPage]);
  const showNextButton = useMemo(() => currentPage < totalPages, [
    currentPage,
    totalPages,
  ]);
  const pages = useMemo(
    () => Array.from(Array(totalPages)).map((_, index) => index + 1),
    [totalPages]
  );

  const visiblePages = useMemo(() => {
    // if number of pages is less than NB_VISIBLE_PAGES
    // show all pages
    if (pages.length < NB_VISIBLE_PAGES) return pages;

    // show nb_visible_pages starting from current page
    const startFromCurrent = pages.slice(
      currentPage - 1,
      NB_VISIBLE_PAGES + currentPage - 1
    );

    // if number of visible pages in startFromCurrent are
    // less than NB_VISIBLE_PAGES, start from the end
    if (startFromCurrent.length < NB_VISIBLE_PAGES)
      return pages.slice(pages.length - NB_VISIBLE_PAGES, pages.length);

    return startFromCurrent;
  }, [currentPage, pages]);

  const showPrevEllipsis = useMemo(() => visiblePages[0] !== pages[0], [
    pages,
    visiblePages,
  ]);

  const showNextEllipsis = useMemo(
    () => visiblePages[visiblePages.length - 1] !== pages[pages.length - 1],
    [pages, visiblePages]
  );

  return (
    <nav className={`pager is-${theme}`}>
      <ul className="pager__list">
        <li>
          <button
            className="pager__page"
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!showPrevButton}
          >
            <span className="visually-hidden">
              {i18nService.copy["a11y.prev"]}
            </span>
            <ChevronLeft />
          </button>
        </li>

        {showPrevEllipsis && <li className="pager__ellipsis">&hellip;</li>}

        {visiblePages.map((page) => (
          <li key={page}>
            <button
              className={classNames("pager__page", [
                currentPage === page && "is-active",
              ])}
              type="button"
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          </li>
        ))}

        {showNextEllipsis && <li className="pager__ellipsis">&hellip;</li>}

        <li>
          <button
            className="pager__page"
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!showNextButton}
          >
            <span className="visually-hidden">
              {i18nService.copy["a11y.next"]}
            </span>
            <ChevronRight />
          </button>
        </li>
      </ul>
    </nav>
  );
};
